import { elapsedTimeContainer } from "./formatted/FormattedDateAndTime.js";
import InfoTip from "./InfoTip.js";
import { formatMass } from "./utils/formatMass.js";
import { isAdminMode } from "./utils/isAdminMode.js";
import {
  ActivityCompactionMetricIcon,
  ActivityCycleMetricIcon,
  ActivityFlowQualityMetricIcon,
  ActivityMetricIconBadge,
  ActivityMetricInfoIcon,
  ActivitySortingErrorsMetricIcon,
} from "./ActivityDetailMetricIcons.js";
import {
  FilledStar,
  EmptyStar,
} from "../ui/components/SortingQualityFooter.js";

const ActivityMetricBlock = ({ icon, title, infoTip, children }) => (
  <article className="bg-white border border-gray-300 rounded-xl flex flex-1 basis-0 min-h-0 gap-3">
    <ActivityMetricIconBadge>{icon}</ActivityMetricIconBadge>
    <div className="flex flex-col flex-1 min-w-0 min-h-0 py-4 pr-4">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-base font-normal text-[#6B7280] leading-6">
          {title}
        </span>
        {infoTip}
      </div>
      <div className="mt-2 flex flex-col flex-1 min-h-0">{children}</div>
    </div>
  </article>
);

const ActivityMetricStars = ({ numberOfStars, size = 12 }) => (
  <div className="flex items-center gap-1">
    {[1, 2, 3].map((star) =>
      star <= (numberOfStars ?? 0) ? (
        <FilledStar key={star} size={size} />
      ) : (
        <EmptyStar key={star} size={size} />
      ),
    )}
  </div>
);

const ActivityDetailMetricsPanel = ({
  t,
  i18n,
  data,
  historyCycle,
  numberOfAnomaly,
  numberOfStars,
  totalVolumeSortingError,
  totalWasteVolume,
  totalMassSortingError,
  numberOfPackmat,
  lastPackmatEfficiency,
}) => (
  <section className="flex flex-col gap-3 min-w-0 w-full h-full flex-1">
    <ActivityMetricBlock
      icon={<ActivityFlowQualityMetricIcon />}
      title={t("Waste category quality")}
      infoTip={
        <InfoTip widthMaxRem={16} icon={<ActivityMetricInfoIcon />}>
          <p className="m-0 text-[13px] leading-tight opacity-90">
            {t(
              "Assessment of material quality based on the volumetric proportion of detected errors",
            )}
            .
          </p>
          <ul className="mt-1 space-y-0.5 text-[13px] leading-none">
            <li className="flex items-center gap-1.5">
              <span className="whitespace-nowrap">
                <i className="far fa-star text-[13px] align-middle"></i>
                <i className="far fa-star text-[13px] ml-0.5"></i>
                <i className="far fa-star text-[13px] ml-0.5 align-middle"></i>
              </span>
              <span className="text-[13px] opacity-90 whitespace-nowrap">
                {t("More than 30% of contaminants")}
              </span>
            </li>
            <li className="flex items-center gap-1.5">
              <span className="whitespace-nowrap">
                <i className="fas fa-star text-[13px] align-middle"></i>
                <i className="far fa-star text-[13px] ml-0.5 align-middle"></i>
                <i className="far fa-star text-[13px] ml-0.5 align-middle"></i>
              </span>
              <span className="text-[13px] opacity-90 whitespace-nowrap">
                {t("Between 15% and 30% of contaminants")}
              </span>
            </li>
            <li className="flex items-center gap-1.5">
              <span className="whitespace-nowrap">
                <i className="fas fa-star text-[13px] align-middle"></i>
                <i className="fas fa-star text-[13px] ml-0.5 align-middle"></i>
                <i className="far fa-star text-[13px] ml-0.5 align-middle"></i>
              </span>
              <span className="text-[13px] opacity-90 whitespace-nowrap">
                {t("Between 5% and 15% of contaminants")}
              </span>
            </li>
            <li className="flex items-center gap-1.5">
              <span className="whitespace-nowrap">
                <i className="fas fa-star text-[13px] align-middle"></i>
                <i className="fas fa-star text-[13px] ml-0.5 align-middle"></i>
                <i className="fas fa-star text-[13px] ml-0.5 align-middle"></i>
              </span>
              <span className="text-[13px] opacity-90 whitespace-nowrap">
                {t("Less than 5% of contaminants")}
              </span>
            </li>
          </ul>
        </InfoTip>
      }
    >
      {data.activatedSortingErrorOption && (
        <div className="flex items-center gap-2">
          <ActivityMetricStars numberOfStars={numberOfStars} size={20} />
          <span className="text-[#5E5E5E]">-</span>
          <span className="text-xl font-bold text-[#5E5E5E]">
            {totalVolumeSortingError > 0
              ? (totalVolumeSortingError * 100) / totalWasteVolume < 1
                ? "<1"
                : Math.round((totalVolumeSortingError * 100) / totalWasteVolume)
              : 0}
            % {t("of contaminants")}
            {isAdminMode() &&
              totalMassSortingError != null &&
              totalMassSortingError > 0 && (
                <> - {formatMass(totalMassSortingError)}</>
              )}
          </span>
        </div>
      )}
    </ActivityMetricBlock>

    <ActivityMetricBlock
      icon={<ActivitySortingErrorsMetricIcon />}
      title={t("Number of sorting errors")}
      infoTip={
        <InfoTip icon={<ActivityMetricInfoIcon />}>
          {t(
            "Anomalies are counted once, even if objects appear multiple times.",
          )}
        </InfoTip>
      }
    >
      <h5 className="text-xl font-bold text-[#222222] m-0">
        {data.activatedSortingErrorOption
          ? numberOfAnomaly
            ? Array.isArray(numberOfAnomaly)
              ? numberOfAnomaly.reduce((sum, val) => sum + (val || 0), 0)
              : numberOfAnomaly
            : 0
          : ""}
      </h5>
    </ActivityMetricBlock>

    {data.packmatActive ? (
      <ActivityMetricBlock
        icon={<ActivityCompactionMetricIcon />}
        title={t("Packmat")}
        infoTip={
          <InfoTip icon={<ActivityMetricInfoIcon />}>
            {t(
              "Number of packmat carried out since the arrival of the container on site and efficiency of the last packmat",
            )}
            .
          </InfoTip>
        }
      >
        <div className="flex items-baseline justify-between gap-2">
          <h5 className="text-xl font-bold text-[#222222] m-0">
            {numberOfPackmat}
          </h5>
          {lastPackmatEfficiency ? (
            <span className="italic text-[13px] text-[#5E5E5E] text-right">
              {t("Efficiency")}: {lastPackmatEfficiency.efficiency}%
            </span>
          ) : null}
        </div>
      </ActivityMetricBlock>
    ) : null}

    <ActivityMetricBlock
      icon={<ActivityCycleMetricIcon />}
      title="Cycle"
      infoTip={
        <InfoTip icon={<ActivityMetricInfoIcon />}>
          {t("Number of days between drop-off date and collection date")}.
        </InfoTip>
      }
    >
      <h5 className="text-xl font-bold text-[#222222] m-0">
        {historyCycle.length > 0
          ? elapsedTimeContainer(
              historyCycle[historyCycle.length - 1]._time,
              i18n.language,
              false,
              new Date(data._time),
            )
          : "-"}
      </h5>
    </ActivityMetricBlock>
  </section>
);

export default ActivityDetailMetricsPanel;
